import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useLanguage } from "../Context/LanguageContext";
import { fetchTempCycleForEvent } from "../utils/eventLogsApi";

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const formatShortDate = (dateStr) => {
  if (!dateStr) return "—";
  const raw = String(dateStr).trim();
  const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    const day = Number(iso[3]);
    const month = Number(iso[2]);
    return `${day} ${MONTHS_SHORT[month - 1] || ""}`.trim();
  }
  const dmy = raw.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    return `${day} ${MONTHS_SHORT[month - 1] || ""}`.trim();
  }
  return raw.slice(0, 10);
};

const formatShortTime = (timeStr) => {
  if (!timeStr) return "—";
  const m = String(timeStr).trim().match(/^(\d{1,2}):(\d{2})/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  return String(timeStr).slice(0, 5);
};

/**
 * Modal: temperature cycle graph for one overheat log event.
 * event = { sensor_id, date, time, threshold?, temperature? }
 */
export default function TempCycleGraphModal({ open, event, serverIp, onClose }) {
  const { t } = useLanguage();
  const [cycleData, setCycleData] = useState(null);
  const [cycleLoading, setCycleLoading] = useState(false);
  const [cycleError, setCycleError] = useState(null);

  const fetchCycle = useCallback(
    async (meta, { silent } = {}) => {
      if (!meta) return;
      if (!silent) {
        setCycleLoading(true);
        setCycleError(null);
      }
      try {
        const data = await fetchTempCycleForEvent(meta);
        setCycleData(data || null);
        if (!data?.found) {
          setCycleError("missing");
        } else if (!(data?.samples || []).length) {
          setCycleError("missing");
        } else {
          setCycleError(null);
        }
      } catch {
        setCycleError("failed");
        if (!silent) setCycleData(null);
      } finally {
        if (!silent) setCycleLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!open || !event) {
      setCycleData(null);
      setCycleError(null);
      setCycleLoading(false);
      return undefined;
    }
    fetchCycle(event);
    return undefined;
  }, [open, event, fetchCycle]);

  useEffect(() => {
    if (!open || !event) return undefined;
    const shouldPoll =
      cycleData?.status === "active" || cycleError === "missing";
    if (!shouldPoll) return undefined;
    const id = setInterval(() => {
      fetchCycle(event, { silent: true });
    }, 2000);
    return () => clearInterval(id);
  }, [open, event, cycleData?.status, cycleError, fetchCycle]);

  const chartPoints = useMemo(() => {
    const samples = cycleData?.samples || [];
    let points = samples.map((s, i) => {
      const fullTime = String(s.t || "");
      const label =
        fullTime.match(/^\d{1,2}:\d{2}:\d{2}/)?.[0] ||
        formatShortTime(fullTime) ||
        `#${i + 1}`;
      const tempNum = Number(s.temp);
      return {
        i,
        label,
        temp: Number.isFinite(tempNum) ? tempNum : null,
        fullTime: fullTime || label,
      };
    }).filter((p) => p.temp != null);

    if (points.length === 0) {
      const tempNum = Number(event?.temperature);
      if (Number.isFinite(tempNum)) {
        const label = formatShortTime(event?.time);
        points = [
          { i: 0, label, temp: tempNum, fullTime: event?.time || label },
        ];
      }
    }

    return points;
  }, [cycleData, event]);

  const thresholdValue = useMemo(() => {
    if (event?.hideThreshold) return null;
    const fromCycle = cycleData?.threshold;
    const fromEvent = event?.threshold;
    const n = Number(fromCycle != null ? fromCycle : fromEvent);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [cycleData, event]);

  const yDomain = useMemo(() => {
    const temps = chartPoints.map((p) => p.temp).filter((n) => Number.isFinite(n));
    if (!temps.length) return ["auto", "auto"];
    let lo = Math.min(...temps);
    let hi = Math.max(...temps);
    if (thresholdValue != null) {
      lo = Math.min(lo, thresholdValue);
      hi = Math.max(hi, thresholdValue);
    }
    const pad = Math.max(2, (hi - lo) * 0.15);
    return [Math.floor(lo - pad), Math.ceil(hi + pad)];
  }, [chartPoints, thresholdValue]);

  const CycleTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0]?.payload;
    if (!point || point.temp == null) return null;
    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
        <p className="text-gray-500 mb-0.5">{point.fullTime || point.label}</p>
        <p className="font-semibold text-red-600">
          {t("logs.temperature")}: {point.temp}°
        </p>
      </div>
    );
  };

  if (!open || !event) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-auto">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              {t("logs.cycleGraphTitle")}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {t("logs.sensor")} ID {event.sensor_id} · {formatShortDate(event.date)}{" "}
              {formatShortTime(event.time)}
              {thresholdValue != null && (
                <>
                  {" "}
                  · {t("logs.threshold")}: {thresholdValue}°
                </>
              )}
            </p>
            {cycleData?.status === "active" && (
              <p className="text-xs font-medium text-amber-700 mt-1">
                {t("logs.cycleActive")}
              </p>
            )}
            {cycleData?.status === "completed" && cycleData?.ended_at && (
              <p className="text-xs text-gray-500 mt-1">
                {t("logs.cycleEnded")}: {formatShortTime(cycleData.ended_at)}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-xl leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-4 py-4 sm:px-5">
          {cycleLoading && (
            <p className="text-center text-gray-500 py-16">{t("logs.cycleLoading")}</p>
          )}

          {!cycleLoading && cycleError === "failed" && chartPoints.length === 0 && (
            <p className="text-center text-red-500 py-16">{t("logs.cycleFailed")}</p>
          )}

          {!cycleLoading && cycleError === "missing" && chartPoints.length === 0 && (
            <p className="text-center text-gray-500 py-16 px-4">
              {t("logs.cycleMissing")}
            </p>
          )}

          {!cycleLoading && !cycleError && chartPoints.length === 0 && (
            <p className="text-center text-gray-500 py-16">{t("logs.cycleNoSamples")}</p>
          )}

          {!cycleLoading && chartPoints.length > 0 && (
            <div className="w-full h-[320px] sm:h-[380px] min-h-[320px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={240} minHeight={280}>
                <LineChart
                  data={chartPoints}
                  margin={{ top: 12, right: 16, left: 0, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="i"
                    type="number"
                    domain={["dataMin", "dataMax"]}
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    tickFormatter={(idx) => {
                      const p = chartPoints[idx];
                      return p?.label || "";
                    }}
                    interval="preserveStartEnd"
                    minTickGap={28}
                    allowDecimals={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    unit="°"
                    width={42}
                    domain={yDomain}
                  />
                  <Tooltip
                    content={<CycleTooltip />}
                    cursor={{ stroke: "#94a3b8", strokeDasharray: "4 4" }}
                  />
                  <Legend />
                  {thresholdValue != null && (
                    <ReferenceLine
                      y={thresholdValue}
                      stroke="#F3A324"
                      strokeDasharray="6 4"
                      label={{
                        value: t("logs.threshold"),
                        fill: "#B45309",
                        fontSize: 11,
                        position: "insideTopRight",
                      }}
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="temp"
                    name={t("logs.temperature")}
                    stroke="#DC2626"
                    strokeWidth={2}
                    dot={chartPoints.length < 40}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
